/** What a creature def is worth to the battle store, as plain functions.
 *
 * Three questions the store asks of every creature it stands up: which flag
 * bits its abilities imply, how many shots it has, how many casts. They live in
 * a module of their own rather than in `init.ts` because both the battle setup
 * and the summoning code need them, and `summon.ts` is reached *from*
 * `spells.ts` - so a declaration in `init.ts` closes
 * init -> spells -> summon -> init. An ESM cycle is not a lint warning; it is a
 * `ReferenceError` the first time a battle starts.
 */

import type { CreatureDef } from "../content/registry.js";
import {
  F_ALIVE, F_FLYING, F_MACHINE_AMMO, F_MACHINE_BREAK, F_MACHINE_HEAL,
  F_NON_LIVING, F_NO_MELEE_PENALTY, F_NO_RETALIATION,
  F_SCHOOL, F_SHOOTER, F_TELEPORTS, F_TWO_HEX, F_UNDEAD, F_WAR_MACHINE,
} from "./state.js";
import { machineFlagsOf } from "./machines.js";

/**
 * Every bit `flagsForCreature` can set - the flags a creature DEFINITION owns,
 * as opposed to the bookkeeping bits the turn machine owns.
 *
 * It lives here, next to the switch it mirrors, because the one caller that
 * needs it is swapping a stack's def mid-battle (Castle's `train` racial) and
 * has to replace exactly these and nothing else: a Master Bowyer that kept a
 * Spearwarden's flags would not be a shooter, and a stack that lost `F_ACTED` in
 * the swap would get a second turn. A list kept in the caller would drift the
 * first time a content-derived flag was added; a list kept here is one line
 * below the line that would add it.
 */
export const CONTENT_FLAG_MASK =
  F_ALIVE | F_FLYING | F_SHOOTER | F_TWO_HEX | F_UNDEAD | F_NON_LIVING
  | F_NO_MELEE_PENALTY | F_NO_RETALIATION | F_TELEPORTS | F_WAR_MACHINE | F_SCHOOL
  | F_MACHINE_BREAK | F_MACHINE_HEAL | F_MACHINE_AMMO;

export function flagsForCreature(def: CreatureDef): number {
  let f = F_ALIVE;
  for (const a of def.abilities) {
    switch (a.kind) {
      case "FLYING": f |= F_FLYING; break;
      case "SHOOTER": f |= F_SHOOTER; break;
      case "TWO_HEX": f |= F_TWO_HEX; break;
      case "UNDEAD": f |= F_UNDEAD; break;
      case "NON_LIVING": f |= F_NON_LIVING; break;
      case "NO_MELEE_PENALTY": f |= F_NO_MELEE_PENALTY; break;
      case "NO_RETALIATION": f |= F_NO_RETALIATION; break;
      case "TELEPORT": f |= F_TELEPORTS | F_FLYING; break;
      case "SIEGE_MACHINE": f |= F_WAR_MACHINE; break;
      // The same bit the catapult's shape earns it: `F_MACHINE_BREAK` is "may
      // batter masonry", and nothing more. What makes the catapult impossible
      // to shoot back at is that it is a breaker with no bow - see
      // `isUntargetable` - so a gun that carries this bit stays a target.
      case "WALL_BREAKER": f |= F_MACHINE_BREAK; break;
      case "SCHOOL": f |= F_SCHOOL; break;
      default: break;
    }
  }
  return f | machineFlagsOf(def);
}

export function shotsForCreature(def: CreatureDef): number {
  for (const a of def.abilities) if (a.kind === "SHOOTER") return a.shots;
  return 0;
}

/**
 * The battle-long cast budget, SUMMED over every `CASTER` the creature carries.
 *
 * It used to return the first one's `casts` and stop. A creature with two
 * spells therefore got a budget sized by whichever entry happened to be first
 * in its JSON array - the enumerator offered both spells, so the feature half
 * worked, and the half that did not was invisible and depended on authoring
 * order. Summing makes two one-cast spells mean two casts, which is the only
 * reading an author can predict from the file.
 */
/** Magic strikes the creature starts a battle with, summed over its abilities. */
export function strikesForCreature(def: CreatureDef): number {
  let n = 0;
  for (const a of def.abilities) if (a.kind === "MAGIC_ATTACK") n += a.uses;
  return n;
}

export function castsForCreature(def: CreatureDef): number {
  let n = 0;
  for (const a of def.abilities) if (a.kind === "CASTER") n += a.casts;
  return n;
}
